import { Injectable } from "@angular/core";
import { BaseZCSChannelService, ChannelStatusData } from "./base-zcs-channel.service";
import { Channel, GetCommunicationChannelForStickQuery, OnDevicesAPISubscription, SendCommandStringForWebMutation } from "ngx-core-services";
import { Router } from "@angular/router";
import { ToastService, ErrorSinkService } from "ngx-zcs-components";
import { ViewportScroller } from "@angular/common";
import gql from "graphql-tag";
import { filter, first } from "rxjs/operators";

import { fromHexStringToUint8Array } from "ngx-modbus";
import { SolarmanService } from "./solarman.service";
import { blockServiceLabels } from "../block-translations.service";

const MQTT_DEBUG = { ENABLED: true };
const mqttLog = (msg: string) => MQTT_DEBUG.ENABLED && console.log(`[Modbus:ZCS] ${msg}`);

@Injectable({
  providedIn: 'root',
})
export class InternetZCSService extends BaseZCSChannelService {
  readonly refreshByDefault = 20 * 60 * 1000;
  protected readonly maxTimeoutInMs = 30 * 1000;
  protected readonly modbusAddress = 1;
  protected readonly delayBetweenCommandsInMs = 1;

  constructor(
    getCommunicationChannelForStickQuery: GetCommunicationChannelForStickQuery,
    onDevicesAPISubscription: OnDevicesAPISubscription,
    router: Router,
    sendCommandStringForWebMutation: SendCommandStringForWebMutation,
    solarmanService: SolarmanService,
    toastService: ToastService,
    viewportScroller: ViewportScroller,
    errorSink: ErrorSinkService,
  ) {
    super(
      getCommunicationChannelForStickQuery,
      onDevicesAPISubscription,
      router,
      solarmanService,
      toastService,
      viewportScroller,
      errorSink,
    );
    this.sendCommandStringForWebMutation = sendCommandStringForWebMutation;
  }

  private readonly sendCommandStringForWebMutation: SendCommandStringForWebMutation;

  protected validateChannel(data: ChannelStatusData): void {
    const isAzzurroMQTTChannel = data.CommunicationChannel === Channel.Azzurro_MQTT;
    const isOnline = data.LoggerStatus === 'connected';

    if (!isAzzurroMQTTChannel || !isOnline) {
      throw new Error(blockServiceLabels.mqttChannelConnectionError);
    }
  }

  async interaction(cmd: Uint8Array): Promise<Uint8Array> {
    if (!this.deviceId) {
      throw new Error(blockServiceLabels.deviceNotFoundError);
    }

    const content = Array.from(cmd).map((x) => x.toString(16).padStart(2, '0')).join('');
    const loggerSn = this.deviceParams?.communication.parentSn;

    mqttLog(`SEND - ${content}`);

    const query = gql`
      fragment sendcommandstringresponse on SendCommandStringResponse {
        collectionTime
        orderId
        success
        code
        msg
        connectStatus
      }
    `;

    const zcsRequest = async () => (await this.sendCommandStringForWebMutation.execute(query, {
      loggerId: loggerSn!,
      deviceSN: this.deviceId!,
      content: [content],
    })).data?.sendCommandStringForWeb;

    const request = await zcsRequest();

    if (request?.success === false) {
      throw new Error(blockServiceLabels.requestFailedError);
    }

    const response = await this.responseByAppsync$.pipe(
      filter((r) => r.orderId === Number(request?.orderId)),
      first(),
    ).toPromise();

    const responseAsUint = fromHexStringToUint8Array(response.ack);
    mqttLog(`RECEIVE - 0x${Array.from(responseAsUint).map((x) => x.toString(16).padStart(2, '0')).join(' ')}`);
    this.response$.next(responseAsUint);
    return responseAsUint;
  }
}
